import DestinationSearch from "@/components/DestinationSearch";
import { citySlug } from "@/lib/travel";
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Flame, Tag, Ticket, Star, Zap, Clock, MapPin, Plane, Trophy, Sparkles } from "lucide-react";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import ShareButton from "@/components/ShareButton";
import FeaturedBrands from "@/components/FeaturedBrands";
import SEO from "@/components/SEO";
import DealCard from "@/components/DealCard";
import ReviewCard from "@/components/ReviewCard";
import CouponCard from "@/components/CouponCard";
import ArticleCard from "@/components/ArticleCard";
import TravelCard from "@/components/TravelCard";
import { cityImage } from "@/lib/cityImages";
import SmartImage from "@/components/SmartImage";
import { mediaUrl, cardImage } from "@/lib/img";
import AdSlot from "@/components/AdSlot";
import { AD_SLOTS } from "@/lib/adSlots";

const HERO_IMG = "https://images.unsplash.com/photo-1758403463575-b1ede3e7abb6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzaG9wcGluZyUyMGFic3RyYWN0fGVufDB8fHx8MTc4ODA4ODAzMHww&ixlib=rb-4.1.0&q=85";


function Section({ title, subtitle, to, linkLabel, children }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-ink-950">{title}</h2>
          {subtitle && <p className="text-neutral-500 mt-1">{subtitle}</p>}
        </div>
        {to && (
          <Link to={to} className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-brand hover:gap-2 transition-all whitespace-nowrap">
            {linkLabel} <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

function msToUtcMidnight() {
  const now = new Date();
  const next = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0);
  return Math.max(0, next - now.getTime());
}

function DealCountdown() {
  const [left, setLeft] = useState(msToUtcMidnight);
  useEffect(() => {
    const t = setInterval(() => setLeft(msToUtcMidnight()), 1000);
    return () => clearInterval(t);
  }, []);
  const pad = (n) => String(n).padStart(2, "0");
  const h = Math.floor(left / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const s = Math.floor((left % 60000) / 1000);
  return (
    <span data-testid="dotd-countdown" className="font-mono tabular-nums font-bold text-white">
      {pad(h)}:{pad(m)}:{pad(s)}
    </span>
  );
}

export default function Home() {
  const { data: deals = [] } = useQuery({ queryKey: ["deals"], queryFn: () => api.get("/deals?limit=8").then(r => r.data) });
  const { data: freshFinds = [] } = useQuery({ queryKey: ["fresh-finds"], queryFn: () => api.get("/deals/fresh-finds?limit=8").then(r => r.data) });
  const { data: dotd } = useQuery({ queryKey: ["dotd"], queryFn: () => api.get("/deal-of-the-day").then(r => r.data) });
  const { data: hot = [] } = useQuery({ queryKey: ["hot"], queryFn: () => api.get("/deals?hot=true&limit=4").then(r => r.data) });
  const { data: coupons = [] } = useQuery({ queryKey: ["coupons"], queryFn: () => api.get("/coupons?limit=3").then(r => r.data) });
  const { data: reviews = [] } = useQuery({ queryKey: ["reviews", "home"], queryFn: () => api.get("/reviews?limit=50").then(r => r.data) });
  const { data: guides = [] } = useQuery({ queryKey: ["guides"], queryFn: () => api.get("/articles?limit=4").then(r => r.data) });
  const { data: travel = [] } = useQuery({ queryKey: ["travel", "home"], queryFn: () => api.get("/travel?limit=100").then(r => r.data) });
  const { data: travelCities = [] } = useQuery({ queryKey: ["travel-cities"], queryFn: () => api.get("/travel/cities").then(r => r.data) });

  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: () => api.get("/categories").then(r => r.data) });

  const { data: roundups = [] } = useQuery({ queryKey: ["reviews", "home-roundups"], queryFn: () => api.get("/reviews?review_type=roundup&limit=12").then(r => r.data) });
  const roundupGuides = (roundups.length ? roundups : reviews.filter((r) => r.review_type === "roundup")).slice(0, 3);
  const singleReviews = reviews.filter((r) => r.review_type !== "roundup").slice(0, 3);
  const editorsPicks = useMemo(() => {
    const g = (guides || []).map((x) => ({ ...x, _kind: "guide" }));
    const r = reviews.filter((x) => x.review_type === "roundup").map((x) => ({ ...x, _kind: "review" }));
    const out = [];
    const max = Math.max(g.length, r.length);
    for (let i = 0; i < max && out.length < 4; i++) {
      if (g[i]) out.push(g[i]);
      if (r[i] && out.length < 4) out.push(r[i]);
    }
    return out;
  }, [guides, reviews]);
  const HEALTH_CATS = ["Health & Beauty", "Health", "Supplements", "Fitness", "Pets", "Pet Health"];
  const trendingSupplements = useMemo(() => reviews
    .filter((r) => HEALTH_CATS.includes(r.category) && r.review_type !== "roundup")
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 4), [reviews]);
  const guideOfWeek = useMemo(() => {
    const rs = reviews.filter((r) => r.review_type === "roundup" && (r.items?.length || 0) > 0);
    return [...rs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
  }, [reviews]);
  const PARTNER_TYPES = ["Experiences", "eSIM", "Transfer", "Insurance"];
  const featuredTravel = [
    ...travel.filter((t) => PARTNER_TYPES.includes(t.travel_type)),
    ...travel.filter((t) => !PARTNER_TYPES.includes(t.travel_type)),
  ].slice(0, 4);

  const featured = hot[0] || deals[0];
  const sideDeals = (hot.length ? hot : deals).slice(1, 3);

  return (
    <PublicLayout>
      <SEO description="GKDeals brings you hand-picked top deals, verified coupon codes and honest product reviews — automated daily to help you save time and money." image={HERO_IMG} />

      {/* HERO */}
      <section className="relative z-20 text-white bg-[#3A0010]">
        {/* decorative layers clipped separately so the search dropdown can overflow the hero */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-br from-[#EF233C] via-[#C40428] to-[#3A0010]" />
          <div className="absolute -top-24 -right-10 w-[34rem] h-[34rem] bg-[#FF5A5F]/35 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-24 w-[36rem] h-[36rem] bg-[#FF8A00]/20 rounded-full blur-3xl" />
          <div className="absolute inset-0 opacity-[0.10]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.6) 1px,transparent 1px)", backgroundSize: "48px 48px" }} />
          <div className="absolute inset-0 grain opacity-50" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 lg:gap-10 items-center">
          {/* LEFT — copy + actions */}
          <div>
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-white/90 bg-white/15 border border-white/20 rounded-full px-4 py-1.5 mb-6 backdrop-blur">
              <Zap className="w-4 h-4 text-[#FFE14D]" /> Updated daily · Automated
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-[4.25rem] font-black tracking-tighter max-w-xl leading-[0.94] drop-shadow-sm">
              The smartest way to <span className="text-[#FFE14D]">save</span> on everything.
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-lg">
              Hand-picked deals, verified coupons and honest product reviews — all in one place, refreshed automatically so you never miss a bargain.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/deals"><button data-testid="hero-deals-btn" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-white text-brand hover:bg-neutral-100 font-bold transition-all hover:-translate-y-0.5 shadow-lg shadow-black/20">Browse Deals <Tag className="w-4 h-4" /></button></Link>
              <Link to="/coupons"><button data-testid="hero-coupons-btn" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 font-semibold transition-colors backdrop-blur">Get Coupons <Ticket className="w-4 h-4" /></button></Link>
            </div>

            {/* trust stats */}
            <div className="flex items-center gap-6 sm:gap-8 mt-8">
              <div>
                <p className="font-display text-2xl font-black tracking-tight">700+</p>
                <p className="text-xs text-white/70 font-medium">Live deals daily</p>
              </div>
              <span className="w-px h-9 bg-white/20" />
              <div>
                <p className="font-display text-2xl font-black tracking-tight">190+</p>
                <p className="text-xs text-white/70 font-medium">Travel deals</p>
              </div>
              <span className="w-px h-9 bg-white/20" />
              <div>
                <p className="font-display text-2xl font-black tracking-tight flex items-center gap-1">100% <Ticket className="w-4 h-4 text-[#FFE14D]" /></p>
                <p className="text-xs text-white/70 font-medium">Verified coupons</p>
              </div>
            </div>

            {/* travel destination search */}
            <DestinationSearch cities={travelCities} />

            {/* category chips */}
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-8">
                {categories.map((c) => (
                  <Link key={c.id} to={`/category/${c.slug}`}
                    className="px-4 py-2 rounded-full bg-white/10 hover:bg-white hover:text-brand border border-white/20 text-sm font-medium transition-colors backdrop-blur" data-testid={`hero-cat-${c.slug}`}>
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT — premium visual with floating cards */}
          <div className="relative hidden lg:block" data-testid="hero-visual">
            <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-white/15 shadow-[0_30px_80px_rgba(0,0,0,0.45)] rotate-[1.5deg]">
              <img src="https://images.pexels.com/photos/14806252/pexels-photo-14806252.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=800" alt="Curated luxury finds" className="w-full h-[480px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3A0010]/70 via-transparent to-[#3A0010]/20" />
            </div>

            {/* floating: deal of the day */}
            <div className="absolute -left-6 top-10 w-60 rounded-2xl bg-white/95 backdrop-blur-xl p-3.5 shadow-2xl shadow-black/40 ring-1 ring-black/5 rotate-[-3deg]">
              <div className="flex items-center gap-3">
                <span className="w-14 h-14 rounded-xl overflow-hidden bg-neutral-100 shrink-0">
                  {dotd?.image_url ? <img src={mediaUrl(dotd.image_url)} alt="" className="w-full h-full object-cover" /> : <span className="w-full h-full flex items-center justify-center"><Flame className="w-6 h-6 text-brand" /></span>}
                </span>
                <div className="min-w-0">
                  <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-bold text-brand"><Flame className="w-3 h-3" /> Deal of the day</span>
                  <p className="font-display font-bold text-ink-900 text-sm leading-tight truncate mt-0.5">{dotd?.title || "Editor's top pick"}</p>
                  {dotd?.discount_percent > 0 && <p className="text-xs font-bold text-emerald-600">−{dotd.discount_percent}% off</p>}
                </div>
              </div>
            </div>

            {/* floating: honest reviews */}
            <div className="absolute -right-4 bottom-10 rounded-2xl bg-ink-950/90 backdrop-blur-xl px-4 py-3 shadow-2xl shadow-black/40 ring-1 ring-white/10 rotate-[3deg]">
              <div className="flex items-center gap-1 text-[#FFE14D]">
                {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-3.5 h-3.5 fill-[#FFE14D]" />)}
              </div>
              <p className="text-white font-display font-bold text-sm mt-1">4.8 · Honest reviews</p>
              <p className="text-white/60 text-[11px]">Real retailers — no Amazon</p>
            </div>
          </div>
        </div>
      </section>

      {/* DEAL OF THE DAY */}
      {dotd && dotd.discount_percent > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 -mt-10 sm:-mt-12">
          <div data-testid="deal-of-the-day" className="rounded-2xl overflow-hidden border border-neutral-200 bg-ink-950 text-white grid md:grid-cols-2 shadow-[0_20px_60px_rgba(0,0,0,0.25)]">
            <div className="relative min-h-[240px] bg-neutral-900">
              {dotd.image_url
                ? <img src={mediaUrl(dotd.image_url)} alt={dotd.title} className="absolute inset-0 w-full h-full object-cover" />
                : <div className="absolute inset-0 bg-gradient-to-br from-[#C40428] to-[#5C0212]" />}
              <span className="absolute top-4 left-4 inline-flex items-center gap-1 bg-brand text-white text-sm font-black px-3 py-1.5 rounded-lg shadow-lg" data-testid="dotd-discount">
                <Flame className="w-4 h-4" /> -{dotd.discount_percent}%
              </span>
            </div>
            <div className="p-6 sm:p-10 flex flex-col justify-center">
              <span className="inline-flex items-center gap-2 self-start text-xs tracking-[0.2em] uppercase font-bold text-[#FFE14D] bg-white/10 border border-white/15 rounded-full px-3 py-1 mb-4">
                <Clock className="w-3.5 h-3.5" /> Deal of the Day
              </span>
              <h3 className="font-display text-2xl sm:text-3xl font-bold leading-tight">{dotd.title}</h3>
              {dotd.description && <p className="text-white/70 mt-3 line-clamp-2 max-w-md">{dotd.description}</p>}
              <div className="flex items-baseline gap-3 mt-5">
                {dotd.price && <span className="font-display font-black text-4xl text-white">{dotd.price}</span>}
                {dotd.original_price && <span className="text-white/50 line-through text-lg">{dotd.original_price}</span>}
              </div>
              <a href={dotd.affiliate_url} target="_blank" rel="noopener noreferrer sponsored" data-testid="dotd-cta"
                className="mt-6 inline-flex items-center justify-center gap-2 self-start h-12 px-8 rounded-full bg-brand hover:bg-brand-hover text-white font-bold transition-colors">
                Grab this deal <ArrowRight className="w-4 h-4" />
              </a>
              {dotd.id && <div className="mt-3"><ShareButton path={`/deal/${dotd.id}`} title={`${dotd.title}${dotd.price ? ` — ${dotd.price}` : ""}`} image={dotd.image_url} variant="pill" testid="dotd-share" /></div>}
              <p className="mt-4 flex items-center gap-2 text-sm text-white/60">
                <Clock className="w-4 h-4 text-[#FFE14D]" /> New deal in <DealCountdown />
              </p>
            </div>
          </div>
        </section>
      )}

      {/* TOP LUXURY BRANDS BAND */}
      <div className="mt-12"><FeaturedBrands variant="hero" /></div>

      {/* EDITOR'S PICKS — best buying advice, surfaced high for readers & reviewers */}
      {editorsPicks.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="editors-picks">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand bg-brand/10 border border-brand/15 rounded-full px-3 py-1 mb-3">
                <Sparkles className="w-3.5 h-3.5" /> Editor's Picks
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-ink-950">Our best buying advice</h2>
              <p className="text-neutral-500 mt-1 text-sm">Hand-picked reviews &amp; guides from our editor, Gordon Kokoma.</p>
            </div>
            <Link to="/reviews" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-brand hover:gap-2 transition-all whitespace-nowrap">
              All reviews &amp; guides <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="editors-picks-grid">
            {editorsPicks.map((item, i) =>
              item._kind === "review"
                ? <ReviewCard key={`ep-r-${item.id || item.slug}`} review={item} index={i} />
                : <ArticleCard key={`ep-g-${item.id || item.slug}`} article={item} index={i} />
            )}
          </div>
        </section>
      )}

      {/* GUIDE OF THE WEEK */}
      {guideOfWeek && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="guide-of-week">
          <div className="rounded-2xl overflow-hidden border border-neutral-200 bg-white grid lg:grid-cols-2 shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
            <div className="relative min-h-[280px] lg:min-h-[380px] bg-neutral-100 order-1 lg:order-none">
              {guideOfWeek.image_url
                ? <img src={mediaUrl(guideOfWeek.image_url)} alt={guideOfWeek.title} className="absolute inset-0 w-full h-full object-cover" />
                : <div className="absolute inset-0 bg-gradient-to-br from-[#C40428] to-[#5C0212]" />}
              <span className="absolute top-4 left-4 bg-ink-950 text-white text-xs font-bold px-2.5 py-1 rounded-md shadow">Buying Guide</span>
            </div>
            <div className="p-6 sm:p-10 flex flex-col justify-center">
              <span className="inline-flex items-center gap-2 self-start text-xs tracking-[0.2em] uppercase font-bold text-brand bg-brand/10 border border-brand/15 rounded-full px-3 py-1 mb-4">
                <Trophy className="w-3.5 h-3.5" /> Guide of the Week
              </span>
              {guideOfWeek.category && <span className="text-xs tracking-[0.15em] uppercase font-bold text-neutral-400 mb-2">{guideOfWeek.category}</span>}
              <h3 className="font-display text-2xl sm:text-3xl font-bold leading-tight text-ink-950">{guideOfWeek.title}</h3>
              {guideOfWeek.summary && <p className="text-neutral-600 mt-3 line-clamp-2 max-w-md">{guideOfWeek.summary}</p>}

              {guideOfWeek.items?.length > 0 && (
                <div className="flex items-center gap-3 mt-5" data-testid="gotw-thumbs">
                  {guideOfWeek.items.slice(0, 4).map((it, i) => (
                    <div key={i} className="w-14 h-14 rounded-lg overflow-hidden border border-neutral-200 bg-neutral-50 shrink-0">
                      {it.image_url && <img src={mediaUrl(it.image_url)} alt={it.name} loading="lazy" className="w-full h-full object-cover" />}
                    </div>
                  ))}
                  <span className="text-sm font-semibold text-neutral-500">+{Math.max(0, (guideOfWeek.items?.length || 0) - 4)} more ranked</span>
                </div>
              )}

              <Link to={`/reviews/${guideOfWeek.slug}`} data-testid="gotw-cta"
                className="mt-6 inline-flex items-center justify-center gap-2 self-start h-12 px-8 rounded-full bg-brand hover:bg-brand-hover text-white font-bold transition-colors">
                See the full guide <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* HOT / BENTO */}
      {featured && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-center gap-2 mb-8">
            <Flame className="w-6 h-6 text-brand" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-ink-950">Hot Right Now</h2>
          </div>
          <div className="grid md:grid-cols-12 gap-6">
            <div className="md:col-span-8">
              <a href={featured.affiliate_url} target="_blank" rel="noopener noreferrer sponsored" data-testid="featured-deal"
                className="group relative block rounded-2xl overflow-hidden h-full min-h-[340px] border border-neutral-200">
                <SmartImage width={1000} src={featured.image_url} alt={featured.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-950/90 via-ink-950/30 to-transparent" />
                <div className="absolute bottom-0 p-6 sm:p-8 text-white">
                  <span className="inline-flex items-center gap-1 bg-brand text-xs font-bold px-2.5 py-1 rounded-md mb-3"><Flame className="w-3 h-3" /> HOT DEAL</span>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold leading-tight max-w-lg">{featured.title}</h3>
                  <div className="flex items-baseline gap-3 mt-3">
                    {featured.price && <span className="font-display font-black text-3xl">{featured.price}</span>}
                    {featured.original_price && <span className="text-neutral-300 line-through">{featured.original_price}</span>}
                  </div>
                </div>
              </a>
            </div>
            <div className="md:col-span-4 grid gap-6">
              {sideDeals.map((d, i) => <DealCard key={d.id} deal={d} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* FRESH FINDS — live Awin product deals */}
      {freshFinds.length > 0 && (
        <Section title="Fresh Finds" subtitle="Just added — live, in-stock luxury picks with your best prices" to="/deals" linkLabel="All deals">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="home-fresh-finds">
            {freshFinds.slice(0, 8).map((d, i) => <DealCard key={d.id} deal={d} index={i} />)}
          </div>
        </Section>
      )}

      {/* TOP COMPARISON GUIDES */}
      {roundupGuides.length > 0 && (
        <div className="bg-white border-y border-neutral-200">
          <Section title="Top Comparison Guides" subtitle="Top 3/5/10 picks — ranked, compared & priced across stores" to="/reviews" linkLabel="All guides">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-buying-guides">
              {roundupGuides.map((r, i) => <ReviewCard key={r.id} review={r} index={i} />)}
            </div>
          </Section>
        </div>
      )}

      {/* TOP DEALS */}
      <Section title="Top Deals For You" subtitle="Fresh savings across every category" to="/deals" linkLabel="All deals">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {deals.slice(0, 8).map((d, i) => <DealCard key={d.id} deal={d} index={i} />)}
        </div>
      </Section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6"><AdSlot slot={AD_SLOTS.homeMid} /></div>

      {/* LATEST BUYING GUIDES (auto-published articles) */}
      {guides.length > 0 && (
        <Section title="Latest Buying Guides" subtitle="Fresh expert guides, auto-published daily to help you buy smarter" to="/guides" linkLabel="All guides">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="home-latest-guides">
            {guides.map((g, i) => <ArticleCard key={g.id} article={g} index={i} />)}
          </div>
        </Section>
      )}

      {/* COUPONS */}
      {coupons.length > 0 && (
        <div className="bg-white border-y border-neutral-200">
          <Section title="Trending Coupons" subtitle="Verified codes, ready to copy" to="/coupons" linkLabel="All coupons">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {coupons.map((c, i) => <CouponCard key={c.id} coupon={c} index={i} />)}
            </div>
          </Section>
        </div>
      )}

      {/* REVIEWS */}
      {singleReviews.length > 0 && (
        <Section title="Latest Product Reviews" subtitle="Honest, in-depth luxury reviews" to="/reviews" linkLabel="All reviews">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-reviews">
            {singleReviews.map((r, i) => <ReviewCard key={r.id} review={r} index={i} />)}
          </div>
        </Section>
      )}

      {/* TRENDING SUPPLEMENTS */}
      {trendingSupplements.length > 0 && (
        <div className="bg-white border-y border-neutral-200">
          <Section title="Trending Supplements" subtitle="Fresh, honest supplement picks — blood sugar, weight, hearing, vision & more" to="/health" linkLabel="All health">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="home-supplements">
              {trendingSupplements.map((r, i) => <ReviewCard key={r.id} review={r} index={i} />)}
            </div>
          </Section>
        </div>
      )}

      {/* TRAVEL */}
      {featuredTravel.length > 0 && (        <Section title="Travel Deals & Experiences" subtitle="Flights, stays, tours, eSIMs & transfers — booked with trusted partners" to="/travel" linkLabel="All travel">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="home-travel">
            {featuredTravel.map((t, i) => <TravelCard key={t.id} item={t} index={i} />)}
          </div>
        </Section>
      )}

      {/* TOP DESTINATIONS */}
      {travelCities.length > 0 && (
        <div className="bg-white border-y border-neutral-200">
          <Section title="Top Destinations" subtitle="Plan your trip city by city — tours, transfers, stays & flights" to="/travel" linkLabel="All travel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="home-destinations">
              {travelCities.slice(0, 8).map((c) => (
                <Link key={c.slug} to={`/travel/${c.slug}`} data-testid={`home-dest-${c.slug}`}
                  className="group relative rounded-2xl overflow-hidden aspect-[4/5] block">
                  <SmartImage src={c.thumb_url || cityImage(c.slug, 600, c.image_url)} alt={c.city}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="font-display font-bold text-white text-lg leading-tight">{c.city}</p>
                    <p className="text-white/75 text-xs font-semibold mt-0.5">{c.count} deal{c.count === 1 ? "" : "s"} & experiences</p>
                  </div>
                </Link>
              ))}
            </div>
          </Section>
        </div>
      )}

      <div className="bg-ink-950 text-white border-t border-white/10" data-testid="home-seo-hub">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Explore GKDeals</h2>
          <p className="text-white/60 mt-1">Browse our hand-picked deals, honest reviews and expert buying guides by category.</p>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div data-testid="hub-categories">
              <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-brand mb-3 flex items-center gap-2"><Tag className="w-4 h-4" /> Shop by category</h3>
              <ul className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <li key={c.id}><Link to={`/category/${c.slug}`} className="inline-block px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white/85 hover:text-white transition-colors">{c.name}</Link></li>
                ))}
              </ul>
            </div>
            <div data-testid="hub-editorial">
              <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-brand mb-3 flex items-center gap-2"><Star className="w-4 h-4" /> Reviews & guides</h3>
              <ul className="flex flex-wrap gap-2">
                <li><Link to="/reviews" className="inline-block px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white/85 hover:text-white transition-colors">Product Reviews</Link></li>
                <li><Link to="/guides" className="inline-block px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white/85 hover:text-white transition-colors">Buying Guides</Link></li>
                <li><Link to="/author/gordon-kokoma" className="inline-block px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white/85 hover:text-white transition-colors">Meet the Editor</Link></li>
              </ul>
            </div>
            <div data-testid="hub-more">
              <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-brand mb-3 flex items-center gap-2"><MapPin className="w-4 h-4" /> More on GKDeals</h3>
              <ul className="flex flex-wrap gap-2">
                <li><Link to="/deals" className="inline-block px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white/85 hover:text-white transition-colors">All Deals</Link></li>
                <li><Link to="/coupons" className="inline-block px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white/85 hover:text-white transition-colors">Coupons</Link></li>
                <li><Link to="/travel" className="inline-block px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white/85 hover:text-white transition-colors">Travel Deals</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
